// Modules
import { toast } from "react-toastify"
import { Socket } from "socket.io-client"

// Types
import { UserType } from "../../../types/user.type"
import { AvailableType, SocketAvailableEventsInterface, } from "../dish.types"
import { SocketAskCloseSession, SocketUser } from "./socket.types";

// PTZ Interfaces
export interface PtzCapabilityRange {
  min: number;
  max: number;
  current: number;
  supported: boolean;
  step: number;
}

export interface PtzPreset {
  id: string;
  name: string;
  position: {
    pan: number;
    tilt: number;
    zoom: number;
  };
}

export interface PtzCapabilities {
  pan: PtzCapabilityRange | false;
  tilt: PtzCapabilityRange | false;
  zoom: PtzCapabilityRange | false;
  presets: PtzPreset[];
}

export interface PtzCurrentPosition {
  pan: number | null;
  tilt: number | null;
  zoom: number | null;
}

export interface PtzDeviceInfo {
  id: string;
  ownerId: string;
  controllerId: string | null;
  status: 'free' | 'controlled' | 'requesting';
  capabilities: PtzCapabilities;
  expiresAt: number | null;
  pendingRequesterId: string | null;
  lastHeartbeat: number;
  currentPosition: PtzCurrentPosition;
}

/**
 * Socket Available
 * @description Socket to listen availables
 * @param {UserType} user
 * @param {Socket} socket
 * @param {SocketAvailableEventsInterface} events
 * @returns {void}
 */
export class SocketAvailable {

    // users availables
    availables: AvailableType[] = []

    constructor(properties: {
        user: UserType,
        socket: Socket,
        events: SocketAvailableEventsInterface
    }) {

        // emit hi to rest of users
        properties.socket.emit('say:hi', {
          user: { ...properties.user },
        })

        // listen welcome
        properties.socket.on('say:welcome', (data: { availables: SocketUser[] }) => {
          properties.events.onAvailables(data?.availables as AvailableType[])
        })

        properties.socket.on('call:outgoing', (_data: any) => {
          // NOTE: This is the event that is emitted when a user calls another user
          const fullName = `${_data.available.user.name}${_data.available.user.surnames ? ` ${_data.available.user.surnames}` : ''}`

          const userLang = localStorage.getItem('language');

          switch (userLang) {
            case 'fr':
              toast(`Appel à ${fullName}`, _data)
              break;
            case 'en':
              toast(`Calling to ${fullName}`, _data)
              break;
            case 'pt':
              toast(`Chamando ${fullName}`, _data)
              break;
            default:
              toast(`Llamando a ${fullName}`, _data)
              break;
          }
        })

        // listen update availables
        properties.socket.on('availables:update', (data: { availables: SocketUser[] }) => {
          properties.events.onAvailables(data?.availables as AvailableType[])

        })

        // listen close session
        properties.socket.on('user:session:close', (data: SocketAskCloseSession) => {

          console.log(`User ${properties.socket.id} is closing session with ${data.user.id}`);
          console.log(data);

          if (data.ask) {
            properties.events.askCloseSession(data.ask)

            return;
          }

          properties.events.closeSession()
        })

        properties.socket.on('user:session:closed', (data: SocketAskCloseSession) => {
          properties.events.askCloseSession(data.ask)
        });

        // listen user accepted your call
        properties.socket.on('call:accepted', (data: any) => {
          const fullName = `${data.available.user.name}${data.available.user.surnames ? ` ${data.available.user.surnames}` : ''}`

          const userLang = localStorage.getItem('language');

          switch (userLang) {
            case 'fr':
              toast(`${fullName} a accepté l'appel`, data)
              break;
            case 'en':
              toast(`${fullName} has accepted the call`, data)
              break;
            case 'pt':
              toast(`${fullName} aceitou a chamada`, data)
              break;
            default:
              toast(`${fullName} ha aceptado la llamada`, data)
              break;
          }

          properties.events.onAccepted(data)
        })

        // listen room created
        properties.socket.on('room:created', (data: any) => {
          const userLang = localStorage.getItem('language');

          const isLocalCall = localStorage.getItem('localCall');

          if (isLocalCall === 'false') {
            switch (userLang) {
              case 'fr':
                toast("Réunion créée")
                break;
              case 'en':
                toast("Meeting created")
                break;
              case 'pt':
                toast("Reunião criada")
                break;
              default:
                toast("Reunión creada")
                break;
            };
          }

          properties.events.onRoom(data.room)

        })

        // listen call to you
        properties.socket.on('call:incoming', (data: any) => {
          properties.events.onCall(data)
        })

        // listen call rejected to you
        properties.socket.on('call:rejected', (data: any) => {
          const fullName = `${data.available.user.name}${data.available.user.surnames ? ` ${data.available.user.surnames}` : ''}`

          const userLang = localStorage.getItem('language');

          switch (userLang) {
            case 'fr':
              toast(`${fullName} a rejeté l'appel`, data)
              break;
            case 'en':
              toast(`${fullName} has rejected the call`, data)
              break;
            case 'pt':
              toast(`${fullName} rejeitou a chamada`, data)
              break;
            default:
              toast(`${fullName} ha rechazado la llamada`, data)
              break;
          }

          properties.events.onRejected(data)

        })

        // listen call canceled
        properties.socket.on('call:canceled', (data: any) => {
          properties.events.onCanceled(data)
        })

        // change layout
        properties.socket.on('room:layout:change', (data: any) => {
          properties.events.onChangeLayout(data);
        })

        // remove stream
        properties.socket.on('stream:remove', (data: any) => {
          properties.events.onRemoveStream(data);
        })

        // change stream status
        properties.socket.on('stream:status_change', (data: any) => {
          properties.events.onChangeStreamStatus(data.streamId, data.status, data.type);
        })

        // Change stream track status
        properties.socket.on('stream:track:change_status', (data: {
          room?: string,
          streamId: string,
          streamType?: string,
          trackId: string,
          status: boolean,
          manual_id?: string,
          type: 'audio' | 'video'
        }) => {
          console.log('stream:track:change_status', data);

          properties.events.onChangeStreamTrackStatus(data.streamId, data.trackId, data.status, data.type, data.room, data.streamType, data.manual_id);
        })

        properties.socket.on('file:share', (data: any) => {
          properties.events.onFileShare(data.username, data.file, data.filename, data.fileUUID);
        })

        properties.socket.on('stream:change_volume', (data: any) => {
          properties.events.onChangeVolume(data.volume);
        })

        properties.socket.on('call:is_no_longer_active', (data: { socketId: string, username: string }) => {
          properties.events.onNoLongerActive(data.socketId, data.username);
        })

        properties.socket.on('ptz:list:response', (data: { devices: PtzDeviceInfo[] }) => {
          properties.events.onPtzDevicesListReceived(data.devices);
        })

        properties.socket.on('ptz:command:move', (data: {
          deviceId: string;
          action: 'pan' | 'tilt' | 'zoom';
          value: number;
        }) => {
          properties.events.onPtzCommandMove(data.deviceId, data.action, data.value);
        })

        properties.socket.on('ptz:list:update', (data: { devices: PtzDeviceInfo[] }) => {
          properties.events.onPtzDevicesListReceived(data.devices);
        });

        properties.socket.on('ptz:capabilities', (data: {
          deviceId: string;
          capabilities: PtzCapabilities;
        }) => {
          properties.events.onPtzCapabilitiesReceived(data.deviceId, data.capabilities);
        });
    }
}
